import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  FileText,
  MessageSquare,
  Plus,
  Trash2,
  Target,
} from 'lucide-react';
import TaskDocumentsPopover from '../components/documents/TaskDocumentsPopover';
import PatientAllergies from '../components/PatientAllergies';
import GoalsTab from '../components/goals/GoalsTab';
import { PatientWounds } from '../components/wounds';
import {
  getPatient,
  getPatientTasks,
  createPatientVisit,
  createOrder,
  updateOrder,
  deleteOrder,
  getAvailableClinicians,
  getPatientWounds,
  getPatientGoalsForVisit,
  getPatientDocuments,
  getIntakes,
  updateVisit,
  deleteVisit,
  client,
  getPatientAllergies,
  getPatientMedications,
  listPatientCommunications,
} from '../services/api';
import PatientDocumentsTab from '../components/documents/PatientDocumentsTab';
import CMS485GenerateDialog from '../components/documents/CMS485GenerateDialog';
import VisitsCalendarPanel from '../components/VisitsCalendarPanel';
import type { PatientDocumentsTabHandle } from '../components/documents/PatientDocumentsTab';
import { PatientMedications } from '../components/medications';
import type {
  PatientResponse,
  TaskListResponse,
  AvailableClinicianResponse as AvailableClinician,
  PatientCommunicationResponse,
  OrderResponse,
} from '../services/api/types.gen';
import type { VisitTypeId } from '../services/api/types.gen';
import { useVisitTypeOptions } from '../hooks/useVisitTypeOptions';
import { useToast } from '../hooks/useToastContext';
import {
  formatDateToMMDDYYYY,
  formatDateToISO,
  parseISOToLocalDate,
  sortByLocalDate,
} from '../utils/dateUtils';
import { getClinicianDisplayName as formatClinicianName } from '../utils/clinicianUtils';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { PatientHeader } from '../components/patient/PatientHeader';
import { PatientInfoTab } from '../components/patient/PatientInfoTab';
import PatientCommunicationsTab from '../components/patient/PatientCommunicationsTab';
import { VisitDetailPanel } from '../components/visit/VisitDetailPanel';
import { OrderDetailPanel } from '../components/order/OrderDetailPanel';
import { LockedTaskIndicator } from '../components/task/LockedTaskIndicator';
import {
  STATUS_DISPLAY_LABELS,
  getStatusBadgeVariant,
} from '../components/task/TaskDetailPanel';
import {
  getScheduleLockedTask,
  type ScheduleLockedTask,
} from '../components/task/lockedTask';
import {
  VISIT_TYPE_DISCIPLINE_MAP,
  isOrderType,
} from '../constants/visitTypes';
import {
  createDefaultTaskTypeFilter,
  filterTaskRows,
  type TaskTypeFilterValue,
} from '../components/task/taskTypeFilters';
import { TaskTypeFilter } from '../components/task/TaskTypeFilter';
import { TaskTypeCombobox } from '../components/task/TaskTypeCombobox';

const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => {
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-8 w-8';
  return (
    <span className='flex items-center gap-1'>
      <div
        className={`animate-spin rounded-full border-b-2 border-primary ${sizeClass}`}
      />
      {text && <span>{text}</span>}
    </span>
  );
};

const PATIENT_TABS = [
  'visits',
  'documents',
  'medications',
  'allergies',
  'wounds',
  'goals',
  'communications',
  'info',
] as const;
type PatientTab = (typeof PATIENT_TABS)[number];

const LOCKED_TASK_ASSIGNEE_TEXT_CLASS =
  'flex h-7 min-w-0 items-center px-2 text-xs text-muted-foreground';

// Mirrors DatePicker datePickerSize='sm': left padding accounts for button
// padding, the calendar icon, and the icon/text gap before the date value.
const LOCKED_TASK_TARGET_DATE_TEXT_CLASS =
  'flex h-6 max-w-[220px] items-center pl-7 pr-1.5 text-xs text-muted-foreground';

const DEFAULT_NEW_TASK_TYPE: VisitTypeId = 'RN_SKILLED_NURSING_VISIT';

function isValidTab(value: string | undefined): value is PatientTab {
  return PATIENT_TABS.includes(value as PatientTab);
}

/** Tab shown on first paint from URL (visit detail route takes precedence). */
function initialTabFromUrl(params: {
  visitId?: string;
  tab?: string;
}): PatientTab {
  if (params.visitId) return 'visits';
  if (isValidTab(params.tab)) return params.tab;
  return 'visits';
}

const PatientDetail: React.FC = () => {
  const {
    id,
    tab: tabParam,
    visitId: visitIdParam,
  } = useParams<{
    id: string;
    tab?: string;
    visitId?: string;
  }>();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const { options: visitTypeOptions } = useVisitTypeOptions();
  const [patient, setPatient] = useState<PatientResponse | null>(null);
  const [tasks, setTasks] = useState<TaskListResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visitsError, setVisitsError] = useState<string | null>(null);
  const [communications, setCommunications] = useState<
    PatientCommunicationResponse[]
  >([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [clinicians, setClinicians] = useState<AvailableClinician[]>([]);
  const [cliniciansLoading, setCliniciansLoading] = useState(false);
  const documentsRef = useRef<PatientDocumentsTabHandle>(null);
  const communicationsFetchedRef = useRef(false);
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCMS485DialogOpen, setIsCMS485DialogOpen] = useState(false);
  const [newTaskType, setNewTaskType] = useState<VisitTypeId>(
    DEFAULT_NEW_TASK_TYPE
  );
  const [tableTaskTypeFilter, setTableTaskTypeFilter] =
    useState<TaskTypeFilterValue>(() => createDefaultTaskTypeFilter());
  const [addTaskTypeFilter, setAddTaskTypeFilter] =
    useState<TaskTypeFilterValue>(() => createDefaultTaskTypeFilter());
  const [newTaskAssignee, setNewTaskAssignee] = useState<string>('');
  const [newTaskDate, setNewTaskDate] = useState<Date | undefined>(new Date());
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const previousPatientIdRef = useRef(id);

  const resetAddTaskForm = useCallback(() => {
    setAddTaskTypeFilter(createDefaultTaskTypeFilter());
    setNewTaskType(DEFAULT_NEW_TASK_TYPE);
    setNewTaskAssignee('');
    setNewTaskDate(new Date());
  }, []);

  const closeAddTaskDialog = useCallback(() => {
    resetAddTaskForm();
    setIsAddDialogOpen(false);
  }, [resetAddTaskForm]);

  useEffect(() => {
    if (previousPatientIdRef.current === id) {
      return;
    }

    previousPatientIdRef.current = id;
    setTableTaskTypeFilter(createDefaultTaskTypeFilter());
    closeAddTaskDialog();
  }, [closeAddTaskDialog, id]);

  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(
    visitIdParam ?? null
  );
  // Active tab for main content area — synced with URL
  const [activeTab, setActiveTabState] = useState<PatientTab>(() =>
    initialTabFromUrl({
      visitId: visitIdParam,
      tab: tabParam,
    })
  );

  const setActiveTab = (tab: PatientTab) => {
    setActiveTabState(tab);
    if (!id) return;
    let path: string;
    if (tab === 'visits' && selectedVisitId) {
      path = `/patients/${id}/visits/${selectedVisitId}`;
    } else if (tab === 'visits') {
      path = `/patients/${id}`;
    } else if (tab === 'communications') {
      path = `/patients/${id}/communications`;
    } else {
      path = `/patients/${id}/${tab}`;
    }
    navigate(path, { replace: true });
  };

  useEffect(() => {
    if (visitIdParam) {
      setSelectedVisitId(visitIdParam);
      setActiveTabState('visits');
      return;
    }
    const resolved = isValidTab(tabParam) ? tabParam : 'visits';
    setActiveTabState(resolved);
    if (resolved === 'visits') {
      setSelectedVisitId(null);
    }
  }, [tabParam, visitIdParam]);

  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const patientData = await getPatient({
          path: { patient_id: id },
          client,
        });
        if (patientData && patientData.data) {
          setPatient(patientData.data);
        }
      } catch (err) {
        console.error('Error fetching patient:', err);
        setError('Failed to load patient details');
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  const fetchTasks = useCallback(async () => {
    if (!id) return;
    try {
      setVisitsLoading(true);
      setVisitsError(null);
      const tasksData = await getPatientTasks({
        path: { patient_id: id },
        client,
      });
      if (tasksData?.data) {
        setTasks(tasksData.data);
      }
    } catch (err) {
      console.error('Error fetching visits/tasks:', err);
      setVisitsError('Failed to load tasks');
    } finally {
      setVisitsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    communicationsFetchedRef.current = false;
    setCommunications([]);
  }, [id]);

  useEffect(() => {
    const loadCommunications = async () => {
      if (!id) return;
      if (activeTab !== 'communications' || communicationsFetchedRef.current) {
        return;
      }
      try {
        setCommunicationsLoading(true);
        const res = await listPatientCommunications({
          path: { patient_id: id },
          client,
        });
        setCommunications(res.data ?? []);
        communicationsFetchedRef.current = true;
      } catch (err) {
        console.error('Error fetching communications:', err);
        setCommunications([]);
      } finally {
        setCommunicationsLoading(false);
      }
    };
    void loadCommunications();
  }, [id, activeTab]);

  useEffect(() => {
    if (!patient) return;
    const fetchClinicians = async () => {
      setCliniciansLoading(true);
      try {
        const response = await getAvailableClinicians({
          client,
          query: patient.clinic_id ? { clinic_id: patient.clinic_id } : {},
        });
        if (response?.data) {
          setClinicians(response.data);
        }
      } finally {
        setCliniciansLoading(false);
      }
    };
    fetchClinicians();
  }, [patient?.clinic_id]);

  const formatDate = (dateString: string | null | undefined) => {
    return formatDateToMMDDYYYY(dateString) || 'N/A';
  };

  const formatStatusLabel = useCallback(
    (status: TaskListResponse['status']) =>
      STATUS_DISPLAY_LABELS[status] ??
      status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase()),
    []
  );

  const isOrder = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'order';
    },
    [tasks]
  );

  const handleTaskUpdate = async (
    taskId: string,
    updates: { assigned_clinician_id?: string | null; date?: string },
    successMessage: string
  ) => {
    setUpdatingTaskId(taskId);

    const refreshTasksAndNotify = async (hasData: boolean) => {
      if (!hasData) return;
      const tasksData = await getPatientTasks({
        path: { patient_id: id! },
        client,
      });
      if (tasksData?.data) setTasks(tasksData.data);
      showSuccess('Task Updated', successMessage);
    };

    try {
      if (isOrder(taskId)) {
        const response = await updateOrder({
          path: { order_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            order_date: updates.date,
          },
          client,
        });
        await refreshTasksAndNotify(!!response.data);
      } else {
        const response = await updateVisit({
          path: { visit_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            visit_date: updates.date,
          },
          client,
        });
        await refreshTasksAndNotify(!!response.data);
      }
    } catch (err) {
      console.error('Error updating task:', err);
      showError('Update Failed', 'Failed to update task.');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleAssigneeChange = (taskId: string, clinicianId: string) => {
    handleTaskUpdate(
      taskId,
      { assigned_clinician_id: clinicianId },
      'Assignee updated successfully.'
    );
  };

  const handleDateChange = (taskId: string, newDate: Date | undefined) => {
    if (!newDate) return;
    const formattedDate = formatDateToISO(newDate);
    if (!formattedDate) return;
    handleTaskUpdate(
      taskId,
      { date: formattedDate },
      'Target date updated successfully.'
    );
  };

  const handleCreateTask = async () => {
    if (!id) return;
    if (!newTaskAssignee) {
      showError('Validation Error', 'Please select an assignee.');
      return;
    }
    if (!newTaskDate) {
      showError('Validation Error', 'Please select a target date.');
      return;
    }

    setIsCreatingTask(true);
    try {
      const taskDate = formatDateToISO(newTaskDate);
      const isOrder = isOrderType(newTaskType);

      const response = isOrder
        ? await createOrder({
            body: {
              patient_id: id,
              order_type: newTaskType,
              order_date: taskDate,
              assigned_clinician_id: newTaskAssignee,
              discipline: VISIT_TYPE_DISCIPLINE_MAP[newTaskType],
            },
            client,
          })
        : await createPatientVisit({
            path: { patient_id: id },
            body: {
              visit_type: newTaskType,
              visit_date: taskDate,
              assigned_clinician_id: newTaskAssignee,
              discipline: VISIT_TYPE_DISCIPLINE_MAP[newTaskType],
            },
            client,
          });

      if (response.error) {
        const detail = (response.error as { detail?: string })?.detail;
        showError('Create Failed', detail || 'Failed to create task.');
        return;
      }
      if (response.data) {
        const tasksData = await getPatientTasks({
          path: { patient_id: id },
          client,
        });
        if (tasksData?.data) setTasks(tasksData.data);
        closeAddTaskDialog();
        showSuccess('Task Created', 'New task added successfully.');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      showError('Create Failed', 'Failed to create task.');
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskId) return;

    setIsDeletingTask(true);
    try {
      if (isOrder(deleteTaskId)) {
        const response = await deleteOrder({
          path: { order_id: deleteTaskId },
          client,
        });
        if (response.error) {
          console.error('Error deleting order:', response.error);
          showError('Delete Failed', 'Failed to delete order.');
          return;
        }
        setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      } else {
        const response = await deleteVisit({
          path: { visit_id: deleteTaskId },
          client,
        });
        if (response.error) {
          console.error('Error deleting task:', response.error);
          showError('Delete Failed', 'Failed to delete task.');
          return;
        }
        setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      }
      setDeleteTaskId(null);
      showSuccess('Task Deleted', 'Task deleted successfully.');
    } catch (err) {
      console.error('Error deleting task:', err);
      showError('Delete Failed', 'Failed to delete task.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const assignableClinicians = useMemo(
    () => clinicians.filter(c => c.is_assignable_clinician),
    [clinicians]
  );

  /** Physicians may not be flagged assignable; communications need them for physician field. */
  const getClinicPhysicians = useMemo(() => {
    return (clinicId: string | null | undefined) => {
      const base = clinicians.filter(c => c.is_physician);
      if (!clinicId) return base;
      return base.filter(c => c.clinic_id === clinicId);
    };
  }, [clinicians]);

  const [caseManagerOverride, setCaseManagerOverride] = useState<
    string | null | undefined
  >(undefined);

  const getClinicAssignableClinicians = useMemo(() => {
    const dedup = (list: typeof assignableClinicians) => {
      const seen = new Set<string>();
      return list.filter(c => {
        if (seen.has(c.user_id)) return false;
        seen.add(c.user_id);
        return true;
      });
    };
    return (clinicId: string | null | undefined) => {
      if (!clinicId) return dedup(assignableClinicians);
      return dedup(assignableClinicians.filter(c => c.clinic_id === clinicId));
    };
  }, [assignableClinicians]);

  const getAssignedClinicianName = useCallback(
    (task: TaskListResponse) => {
      if (!task.assigned_clinician_id) return 'Unassigned';

      if (task.assigned_clinician_name) {
        return task.assigned_clinician_name;
      }

      const clinician = getClinicAssignableClinicians(
        task.agency_clinic_id
      ).find(c => c.id === task.assigned_clinician_id);

      return clinician
        ? formatClinicianName(clinician)
        : 'Assigned clinician unavailable';
    },
    [getClinicAssignableClinicians]
  );

  const renderStatusBadge = useCallback(
    (
      task: TaskListResponse,
      lockedTask: ScheduleLockedTask | null,
      className?: string
    ) => (
      <span className='inline-flex items-center gap-1.5'>
        <Badge
          variant={getStatusBadgeVariant(task.status)}
          className={className}
        >
          {formatStatusLabel(task.status)}
        </Badge>
        {lockedTask && (
          <span onClick={e => e.stopPropagation()}>
            <LockedTaskIndicator
              taskType={lockedTask.task_type}
              status={lockedTask.status}
            />
          </span>
        )}
      </span>
    ),
    [formatStatusLabel]
  );

  // Names that appear more than once (different users, same display name).
  // Deduplicate by user_id first to avoid false positives from the same
  // clinician appearing in multiple clinics.
  const duplicateClinicianNames = useMemo(() => {
    const seen = new Set<string>();
    const deduped = assignableClinicians.filter(c => {
      if (seen.has(c.user_id)) return false;
      seen.add(c.user_id);
      return true;
    });
    const counts = new Map<string, number>();
    for (const c of deduped) {
      const name = formatClinicianName(c);
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    const dupes = new Set<string>();
    for (const [name, count] of counts) {
      if (count > 1) dupes.add(name);
    }
    return dupes;
  }, [assignableClinicians]);

  const allTasks = useMemo(
    () => sortByLocalDate(tasks, 'task_date', 'asc'),
    [tasks]
  );

  const filteredTasks = useMemo(
    () => filterTaskRows(allTasks, tableTaskTypeFilter),
    [allTasks, tableTaskTypeFilter]
  );

  const selectedTask = useMemo(
    () => tasks.find(t => t.id === selectedVisitId) ?? null,
    [tasks, selectedVisitId]
  );

  const handleVisitUpdated = useCallback(async () => {
    if (!id) return;
    const tasksData = await getPatientTasks({
      path: { patient_id: id },
      client,
    });
    if (tasksData?.data) setTasks(tasksData.data);
  }, [id]);

  const handleMedicationOrderCreated = useCallback(
    async (order: OrderResponse) => {
      if (!id) return;
      setSelectedVisitId(order.id);
      setActiveTabState('visits');
      navigate(`/patients/${id}/visits/${order.id}`);
      showSuccess(
        'Medication Order Created',
        'Medication order created successfully.'
      );
      try {
        const tasksData = await getPatientTasks({
          path: { patient_id: id },
          client,
        });
        if (tasksData?.data) setTasks(tasksData.data);
      } catch (error) {
        console.warn(
          'Failed to refresh patient tasks after medication order',
          error
        );
      }
    },
    [id, navigate, showSuccess]
  );

  const latestEpisodeClinicId = useMemo(() => {
    if (tasks.length === 0) return undefined;
    const taskWithLatestEpisode = sortByLocalDate(
      tasks,
      'episode_start_date',
      'desc'
    )[0];
    return taskWithLatestEpisode?.agency_clinic_id ?? undefined;
  }, [tasks]);

  const latestEpisode = useMemo(() => {
    if (!tasks.length)
      return {
        id: null,
        start: null,
        end: null,
        clinicianName: null,
        caseManagerName: null,
      };
    const latest = [...tasks].sort((a, b) =>
      (b.episode_start_date ?? '').localeCompare(a.episode_start_date ?? '')
    )[0];
    return {
      id: latest.episode_id ?? null,
      start: latest.episode_start_date ?? null,
      end: latest.episode_end_date ?? null,
      clinicianName: latest.episode_assigned_clinician_name ?? null,
      caseManagerName: latest.episode_case_manager_name ?? null,
    };
  }, [tasks]);

  const handleCreateVisitFromCalendar = async (
    date: Date,
    visitType: VisitTypeId,
    clinicianId?: string
  ) => {
    if (!id) {
      showError('Create Failed', 'Failed to create visit.');
      return false;
    }
    try {
      const response = await createPatientVisit({
        path: { patient_id: id },
        body: {
          visit_type: visitType,
          visit_date: formatDateToISO(date),
          assigned_clinician_id: clinicianId ?? null,
          discipline: VISIT_TYPE_DISCIPLINE_MAP[visitType],
        },
        client,
      });
      if (response.error) {
        console.error('Failed to create visit:', response.error);
        showError('Create Failed', 'Failed to create visit.');
        return false;
      }
      if (response.data) {
        try {
          const tasksData = await getPatientTasks({
            path: { patient_id: id },
            client,
          });
          if (tasksData?.data) setTasks(tasksData.data);
        } catch (refreshError) {
          console.warn(
            'Failed to refresh patient tasks after creating visit:',
            refreshError
          );
        }
        showSuccess('Visit Created', 'New visit added successfully.');
        return true;
      }
      console.error('Create visit returned no data and no error:', response);
      showError('Create Failed', 'Failed to create visit.');
      return false;
    } catch (error) {
      console.error('Failed to create visit:', error);
      showError('Create Failed', 'Failed to create visit.');
      return false;
    }
  };

  const [medicationCount, setMedicationCount] = useState(0);
  const [allergyCount, setAllergyCount] = useState(0);
  const [documentCount, setDocumentCount] = useState(0);
  const [woundCount, setWoundCount] = useState(0);
  const [activeGoalCount, setActiveGoalCount] = useState(0);

  useEffect(() => {
    if (!id) return;
    const fetchCounts = async () => {
      try {
        const [medsRes, allergiesRes, woundsRes, patientDocsRes, intakesRes] =
          await Promise.all([
            getPatientMedications({
              path: { patient_id: id },
              query: { include_discontinued: false },
              client,
            }),
            getPatientAllergies({
              path: { patient_id: id },
              client,
            }),
            getPatientWounds({
              path: { patient_id: id },
              query: { include_discontinued: false },
              client,
            }),
            getPatientDocuments({
              path: { patient_id: id },
              query: { include_task_documents: true },
              client,
            }),
            getIntakes({
              query: { patient_id: id },
              client,
            }),
          ]);

        const intakeDocumentCount =
          intakesRes?.data?.items?.reduce(
            (total, intake) =>
              total +
              (intake.documents?.filter(d => !d.task_type_tag).length ?? 0),
            0
          ) ?? 0;
        setMedicationCount(medsRes?.data?.length ?? 0);
        setAllergyCount(allergiesRes?.data?.length ?? 0);
        setWoundCount(woundsRes?.data?.length ?? 0);
        const patientDocCount =
          patientDocsRes?.data?.filter(d => d.document_type !== 'intake')
            .length ?? 0;
        setDocumentCount(patientDocCount + intakeDocumentCount);
      } catch {
        // Counts are non-critical
      }
    };
    fetchCounts();
  }, [id, activeTab]);

  useEffect(() => {
    if (!selectedVisitId) {
      setActiveGoalCount(0);
      return;
    }
    const selectedTaskForGoals = tasks.find(t => t.id === selectedVisitId);
    if (selectedTaskForGoals?.task_type !== 'visit') {
      setActiveGoalCount(0);
      return;
    }
    const fetchActiveGoalCount = async () => {
      try {
        const goalsRes = await getPatientGoalsForVisit({
          path: { visit_id: selectedVisitId },
          client,
        });
        const goals = goalsRes?.data ?? [];
        setActiveGoalCount(
          goals.filter(goal => !goal.is_met && !goal.is_unattainable).length
        );
      } catch {
        setActiveGoalCount(0);
      }
    };
    fetchActiveGoalCount();
  }, [selectedVisitId, activeTab, tasks]);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <LoadingSpinner size='md' />
          </div>
          <p className='text-muted-foreground'>Loading patient details...</p>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Patient</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Patient not found'}
          </p>
          <Button onClick={() => navigate('/patients')} variant='outline'>
            <ArrowLeft className='h-4 w-4' />
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-background min-h-full flex flex-col'>
      <PatientHeader
        patient={patient}
        caseManagerName={
          caseManagerOverride !== undefined
            ? caseManagerOverride
            : latestEpisode.caseManagerName
        }
        assignmentsLoading={
          visitsLoading || cliniciansLoading || communicationsLoading
        }
      >
        {/* Tab Navigation */}
        <div className='px-6 flex items-center justify-between gap-3 pb-1'>
          <div className='flex items-center gap-1.5'>
            {(
              [
                {
                  id: 'visits' as const,
                  label: 'Visits',
                  count: tasks.length,
                },
                {
                  id: 'documents' as const,
                  label: 'Documents',
                  count: documentCount,
                },
                {
                  id: 'medications' as const,
                  label: 'Medications',
                  count: medicationCount,
                },
                {
                  id: 'allergies' as const,
                  label: 'Allergies',
                  count: allergyCount,
                },
                {
                  id: 'wounds' as const,
                  label: 'Wounds',
                  count: woundCount,
                },
                {
                  id: 'goals' as const,
                  label: 'Goals',
                  count: activeGoalCount,
                },
                {
                  id: 'communications' as const,
                  label: 'Communications',
                  count: communications.length,
                },
                {
                  id: 'info' as const,
                  label: 'Patient Info',
                },
              ] as const
            ).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 rounded-t-md text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-primary text-foreground bg-muted/60'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border hover:bg-muted/30'
                }`}
              >
                {tab.label}
                {'count' in tab && tab.count > 0 && (
                  <Badge
                    variant='secondary'
                    className='ml-1.5 text-[10px] px-1.5 py-0 h-4 min-w-4 inline-flex items-center justify-center'
                  >
                    {tab.count}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>
      </PatientHeader>

      {/* Main Content */}
      <div
        className={
          activeTab === 'visits' && selectedVisitId
            ? 'flex-1 flex flex-col min-h-0'
            : 'p-5'
        }
      >
        {/* ─── Visit / Order Detail ─── */}
        {activeTab === 'visits' &&
        selectedVisitId &&
        id &&
        selectedTask?.task_type === 'order' ? (
          <OrderDetailPanel
            orderId={selectedVisitId}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
          />
        ) : activeTab === 'visits' && selectedVisitId && id ? (
          <VisitDetailPanel
            visitId={selectedVisitId}
            patientId={id}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
            onVisitUpdated={handleVisitUpdated}
          />
        ) : (
          activeTab === 'visits' && (
            <Card className='border-border/70 shadow-sm'>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardDescription>
                    {allTasks.length > 0 && allTasks[0].episode_start_date ? (
                      <span className='flex flex-col'>
                        <span>
                          Episode: {formatDate(allTasks[0].episode_start_date)}{' '}
                          - {formatDate(allTasks[0].episode_end_date || '')}
                        </span>
                        {(() => {
                          const firstTask = allTasks[0];
                          const clinicianName =
                            firstTask.episode_assigned_clinician_name;
                          const caseManagerName =
                            firstTask.episode_case_manager_name;
                          if (!clinicianName && !caseManagerName) return null;
                          return (
                            <span className='text-xs'>
                              {clinicianName && <>Clinician: {clinicianName}</>}
                              {clinicianName && caseManagerName && ' · '}
                              {caseManagerName && (
                                <>Case Manager: {caseManagerName}</>
                              )}
                            </span>
                          );
                        })()}
                      </span>
                    ) : (
                      'Episode tasks and assignments'
                    )}
                  </CardDescription>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setIsCMS485DialogOpen(true)}
                    >
                      <FileText className='h-4 w-4' />
                      Generate CMS-485
                    </Button>
                    <Button size='sm' onClick={() => setIsAddDialogOpen(true)}>
                      <Plus className='h-4 w-4' />
                      Add Task
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className='pt-3'>
                <VisitsCalendarPanel
                  visits={allTasks}
                  episodeStartDate={latestEpisode.start}
                  episodeEndDate={latestEpisode.end}
                  onVisitClick={task =>
                    navigate(`/patients/${id}/visits/${task.id}`)
                  }
                  onCreateVisit={handleCreateVisitFromCalendar}
                  patientId={id!}
                  clinicians={assignableClinicians}
                  duplicateClinicianNames={duplicateClinicianNames}
                />
                <div className='mt-4 border-t border-border/60 pt-4'>
                  {visitsLoading ? (
                    <div className='text-center py-8'>
                      <div className='flex justify-center mb-4'>
                        <LoadingSpinner size='md' />
                      </div>
                      <p className='text-muted-foreground'>Loading tasks...</p>
                    </div>
                  ) : visitsError ? (
                    <div className='py-8 text-center text-muted-foreground'>
                      <AlertTriangle className='h-12 w-12 mx-auto mb-4 opacity-50' />
                      <p>{visitsError}</p>
                    </div>
                  ) : allTasks.length === 0 ? (
                    <div className='py-8 text-center text-muted-foreground'>
                      <Activity className='h-12 w-12 mx-auto mb-4 opacity-50' />
                      <p>No tasks found for this patient</p>
                    </div>
                  ) : (
                    <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
                      <TaskTypeFilter
                        variant='inline'
                        value={tableTaskTypeFilter}
                        onChange={setTableTaskTypeFilter}
                        totalCount={allTasks.length}
                        filteredCount={filteredTasks.length}
                      />
                      <div className='hidden lg:grid lg:grid-cols-[minmax(150px,1fr)_minmax(170px,1fr)_minmax(150px,0.9fr)_180px_36px_36px_48px] items-center gap-3 border-b border-border/60 bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
                        <span>Task</span>
                        <span>Assignee</span>
                        <span>Target Date</span>
                        <span>Status</span>
                        <span></span>
                        <span></span>
                        <span>Actions</span>
                      </div>
                      {filteredTasks.length === 0 ? (
                        <div className='py-8 text-center text-muted-foreground'>
                          No tasks match these filters.
                        </div>
                      ) : (
                        <div className='divide-y divide-border/60'>
                          {filteredTasks.map(task => {
                            const lockedTask = getScheduleLockedTask(task);
                            const isLockedTask = lockedTask !== null;

                            return (
                              <div
                                key={task.id}
                                data-testid={`task-row-${task.id}`}
                                className='group cursor-pointer px-3 py-2.5 transition-colors hover:bg-muted/35'
                                onClick={() =>
                                  navigate(`/patients/${id}/visits/${task.id}`)
                                }
                              >
                                <div className='grid gap-y-2.5 lg:grid-cols-[minmax(150px,1fr)_minmax(170px,1fr)_minmax(150px,0.9fr)_180px_36px_36px_48px] lg:items-center lg:gap-x-3'>
                                  <div className='min-w-0'>
                                    <div className='truncate text-sm font-medium text-foreground'>
                                      {task.display_name}
                                    </div>
                                    <div className='mt-0.5 flex items-center gap-2 text-xs text-muted-foreground'>
                                      <span className='lg:hidden'>
                                        {renderStatusBadge(
                                          task,
                                          lockedTask,
                                          'text-[10px]'
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    onClick={
                                      isLockedTask
                                        ? undefined
                                        : e => e.stopPropagation()
                                    }
                                    className='min-w-0 space-y-1'
                                  >
                                    <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                                      Assignee
                                    </span>
                                    {isLockedTask ? (
                                      <span
                                        className={
                                          LOCKED_TASK_ASSIGNEE_TEXT_CLASS
                                        }
                                      >
                                        <span className='truncate'>
                                          {getAssignedClinicianName(task)}
                                        </span>
                                      </span>
                                    ) : (
                                      <Select
                                        value={
                                          task.assigned_clinician_id || 'none'
                                        }
                                        onValueChange={value =>
                                          handleAssigneeChange(task.id, value)
                                        }
                                        disabled={updatingTaskId === task.id}
                                      >
                                        <SelectTrigger
                                          selectSize='sm'
                                          className='w-full'
                                        >
                                          {task.assigned_clinician_id ? (
                                            <SelectValue />
                                          ) : (
                                            <span className='text-muted-foreground'>
                                              Unassigned
                                            </span>
                                          )}
                                        </SelectTrigger>
                                        <SelectContent>
                                          {getClinicAssignableClinicians(
                                            task.agency_clinic_id
                                          ).map(clinician => (
                                            <SelectItem
                                              key={clinician.id}
                                              value={clinician.id}
                                            >
                                              {formatClinicianName(clinician)}
                                              {duplicateClinicianNames.has(
                                                formatClinicianName(clinician)
                                              ) && (
                                                <span className='ml-1 text-xs text-muted-foreground'>
                                                  ({clinician.user_email})
                                                </span>
                                              )}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                  </div>
                                  <div
                                    onClick={
                                      isLockedTask
                                        ? undefined
                                        : e => e.stopPropagation()
                                    }
                                    className='space-y-1'
                                  >
                                    <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                                      Target Date
                                    </span>
                                    {isLockedTask ? (
                                      <span
                                        className={
                                          LOCKED_TASK_TARGET_DATE_TEXT_CLASS
                                        }
                                      >
                                        {formatDate(task.task_date)}
                                      </span>
                                    ) : (
                                      <DatePicker
                                        date={
                                          task.task_date
                                            ? parseISOToLocalDate(
                                                task.task_date
                                              )
                                            : undefined
                                        }
                                        onDateChange={(
                                          date: Date | undefined
                                        ) => handleDateChange(task.id, date)}
                                        disabled={updatingTaskId === task.id}
                                        datePickerSize='sm'
                                        className='w-full max-w-[220px]'
                                        minDate={
                                          task.episode_start_date
                                            ? parseISOToLocalDate(
                                                task.episode_start_date
                                              )
                                            : undefined
                                        }
                                        maxDate={
                                          task.episode_end_date
                                            ? parseISOToLocalDate(
                                                task.episode_end_date
                                              )
                                            : undefined
                                        }
                                      />
                                    )}
                                  </div>
                                  <div className='hidden lg:block'>
                                    {renderStatusBadge(
                                      task,
                                      lockedTask,
                                      'text-xs'
                                    )}
                                  </div>
                                  <div
                                    onClick={e => e.stopPropagation()}
                                    className='hidden lg:block'
                                  >
                                    {task.notes ? (
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button className='rounded p-1 hover:bg-muted'>
                                            <MessageSquare className='h-4 w-4 text-blue-500' />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className='max-w-sm'>
                                          <p className='whitespace-pre-wrap text-sm'>
                                            {task.notes}
                                          </p>
                                        </PopoverContent>
                                      </Popover>
                                    ) : null}
                                  </div>
                                  <div
                                    onClick={e => e.stopPropagation()}
                                    className='hidden lg:block'
                                  >
                                    <TaskDocumentsPopover
                                      taskId={task.id}
                                      initialCount={task.document_count}
                                    />
                                  </div>
                                  <div
                                    onClick={
                                      isLockedTask
                                        ? undefined
                                        : e => e.stopPropagation()
                                    }
                                    className='justify-self-start flex gap-1'
                                  >
                                    {!isLockedTask && (
                                      <Button
                                        variant='ghost'
                                        size='sm'
                                        aria-label={`Delete ${task.display_name}`}
                                        onClick={() => setDeleteTaskId(task.id)}
                                        disabled={updatingTaskId === task.id}
                                        className='h-8 w-8 p-0 opacity-70 transition-opacity group-hover:opacity-100'
                                      >
                                        <Trash2 className='h-4 w-4 text-destructive' />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <PatientDocumentsTab ref={documentsRef} patientId={id!} />
        )}

        {/* ─── Medications Tab ─── */}
        {activeTab === 'medications' && (
          <PatientMedications
            patientId={id!}
            onMedicationOrderCreated={handleMedicationOrderCreated}
            onDocumentCreated={() => documentsRef.current?.refreshDocuments()}
          />
        )}

        {/* Allergies Tab */}
        {activeTab === 'allergies' && <PatientAllergies patient={patient} />}

        {/* Visit-scoped context (Wounds + Goals) */}
        {(activeTab === 'wounds' || activeTab === 'goals') && (
          <div className='mb-4 mt-1'>
            <div className='flex items-center gap-2'>
              <Select
                value={selectedVisitId ?? ''}
                onValueChange={v => setSelectedVisitId(v || null)}
              >
                <SelectTrigger
                  selectSize='sm'
                  className={`w-[360px] ${
                    !selectedVisitId
                      ? 'border-destructive/60 text-destructive'
                      : ''
                  }`}
                >
                  <SelectValue placeholder='Select visit' />
                </SelectTrigger>
                <SelectContent>
                  {[...allTasks]
                    .sort(
                      (a, b) =>
                        new Date(b.task_date ?? b.scheduled_date).getTime() -
                        new Date(a.task_date ?? a.scheduled_date).getTime()
                    )
                    .map(task => (
                      <SelectItem key={task.id} value={task.id}>
                        {task.display_name} -{' '}
                        {formatDate(task.task_date ?? task.scheduled_date)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {selectedVisitId && (
                <button
                  onClick={() => setSelectedVisitId(null)}
                  className='text-xs text-muted-foreground underline hover:text-foreground'
                >
                  Clear
                </button>
              )}
            </div>
            {!selectedVisitId && (
              <p className='mt-2 inline-flex items-center gap-1.5 text-xs text-destructive'>
                <AlertCircle className='h-3.5 w-3.5' />
                Select a visit to enable{' '}
                {activeTab === 'wounds' ? 'wound actions' : 'goals'}.
              </p>
            )}
          </div>
        )}

        {/* Wounds Tab */}
        {activeTab === 'wounds' && (
          <PatientWounds
            patientId={id!}
            visitId={selectedTask?.id}
            visits={allTasks}
          />
        )}

        {/* Goals Tab */}
        {activeTab === 'goals' &&
          (selectedTask ? (
            <div className='h-[calc(100vh-10rem)]'>
              <GoalsTab
                open={true}
                onClose={() => {}}
                patientFirstName={patient.first_name}
                patientLastName={patient.last_name}
                visit={selectedTask}
                inline={true}
              />
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
              <Target className='h-12 w-12 mb-4 opacity-30' />
              <p className='text-base font-medium'>
                Select a visit to get started.
              </p>
            </div>
          ))}

        {activeTab === 'communications' && id && (
          <PatientCommunicationsTab
            patientId={id}
            patientClinicId={patient?.clinic_id}
            communications={communications}
            communicationsLoading={communicationsLoading}
            onCommunicationsChange={setCommunications}
            formatClinicianName={formatClinicianName}
            getClinicAssignableClinicians={getClinicAssignableClinicians}
            getClinicPhysicians={getClinicPhysicians}
          />
        )}

        {/* ─── Patient Info Tab ─── */}
        {activeTab === 'info' && (
          <PatientInfoTab
            patient={patient}
            onPatientUpdated={updated => setPatient(updated)}
            onCaseManagerUpdated={name => setCaseManagerOverride(name)}
          />
        )}
      </div>

      {/* CMS-485 Generate Dialog */}
      <CMS485GenerateDialog
        open={isCMS485DialogOpen}
        onOpenChange={setIsCMS485DialogOpen}
        patientId={id!}
        episodeId={latestEpisode.id ?? undefined}
        onSuccess={() => documentsRef.current?.refreshDocuments()}
        onError={showError}
        onGenerated={showSuccess}
      />

      {/* Add Task Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={open => {
          if (open) {
            setIsAddDialogOpen(true);
            return;
          }
          closeAddTaskDialog();
        }}
      >
        <DialogContent className='overflow-visible'>
          <DialogHeader>
            <DialogTitle>Add New Task</DialogTitle>
            <DialogDescription className='sr-only'>
              Select a task type, assignee, and date for this episode.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div>
              <label className='text-sm font-medium'>Task Type</label>
              <div className='mt-1'>
                <TaskTypeCombobox
                  options={visitTypeOptions}
                  value={newTaskType}
                  onValueChange={setNewTaskType}
                  filter={addTaskTypeFilter}
                  onFilterChange={setAddTaskTypeFilter}
                  disabled={isCreatingTask}
                />
              </div>
            </div>

            <div>
              <label className='text-sm font-medium'>Assignee</label>
              <Select
                value={newTaskAssignee}
                onValueChange={setNewTaskAssignee}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder='Select assignee' />
                </SelectTrigger>
                <SelectContent
                  position='item-aligned'
                  side='bottom'
                  align='center'
                  sideOffset={4}
                  collisionPadding={16}
                  className='max-h-64 w-[min(var(--radix-select-trigger-width),calc(100vw-3rem))] max-w-[calc(100vw-3rem)]'
                >
                  {getClinicAssignableClinicians(latestEpisodeClinicId).map(
                    clinician => (
                      <SelectItem key={clinician.id} value={clinician.id}>
                        {formatClinicianName(clinician)}
                        {duplicateClinicianNames.has(
                          formatClinicianName(clinician)
                        ) && (
                          <span className='ml-1 text-xs text-muted-foreground'>
                            ({clinician.user_email})
                          </span>
                        )}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className='text-sm font-medium'>Target Date</label>
              <div className='mt-1'>
                <DatePicker
                  date={newTaskDate}
                  onDateChange={setNewTaskDate}
                  minDate={
                    latestEpisode.start
                      ? parseISOToLocalDate(latestEpisode.start)
                      : undefined
                  }
                  maxDate={
                    latestEpisode.end
                      ? parseISOToLocalDate(latestEpisode.end)
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={closeAddTaskDialog}
              disabled={isCreatingTask}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              disabled={isCreatingTask || !newTaskAssignee}
            >
              {isCreatingTask ? 'Creating...' : 'Add Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTaskId}
        onOpenChange={open => !open && setDeleteTaskId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            Are you sure you want to delete this task? This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteTaskId(null)}
              disabled={isDeletingTask}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteTask}
              disabled={isDeletingTask}
            >
              {isDeletingTask ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDetail;
